import { filterReportByRole, reportRoles, resultStatus, } from "@gearbox-protocol/liquidator-v2-config";
import { AddressMap, hexEq, TypedObjectUtils, } from "@gearbox-protocol/sdk/onchain";
import { createSelector } from "@reduxjs/toolkit";
import { bin } from "d3-array";
import { describeCreditManager, filterResultsByMarketConfigurator, getAccountStatuses, histogramLabel, isAccountWhitelisted, } from "./report-helpers";
/**
 * Full report with tracks of all roles
 */
function rawReportSelector(state) {
    return state.report;
}
export function roleSelector(state) {
    return state.role;
}
/**
 * Report with tracks and results of the active role only
 */
export const reportSelector = createSelector(rawReportSelector, roleSelector, filterReportByRole);
export function marketConfiguratorSelector(state) {
    return state.marketConfigurator;
}
export const tracksSelector = createSelector(reportSelector, state => state.tracks);
function buildWhitelist({ whitelist }) {
    return new AddressMap(whitelist?.map(w => [w.address, w.reason ?? "whitelisted"]));
}
/**
 * Whitelist applies to service role only
 */
const whitelistSelector = createSelector(rawReportSelector, roleSelector, (report, role) => role === "service" ? buildWhitelist(report) : new AddressMap());
const accountLabelsSelector = createSelector(reportSelector, ({ accountLabels }) => {
    return new AddressMap(accountLabels ? TypedObjectUtils.entries(accountLabels) : undefined);
});
export function tracksFiltersSelector(state) {
    return state.trackFilters;
}
export const sdkStateSelector = createSelector(reportSelector, (state) => state.sdkState);
export const visibleTracksSelector = createSelector(tracksSelector, tracksFiltersSelector, (tracks, filter) => tracks.filter(t => !!filter[t.id]));
/**
 * Filter results by market configurator
 */
const marketConfiguratorResultsSelector = createSelector(reportSelector, marketConfiguratorSelector, (report, marketConfigurator) => filterResultsByMarketConfigurator(report.results, report.sdkState, marketConfigurator));
/**
 * Results of visible tracks, regardless of error filters
 */
const visibleResultsSelector = createSelector(marketConfiguratorResultsSelector, tracksFiltersSelector, (results, trackFilters) => results.filter(r => !!trackFilters[r.trackId]));
/**
 * Status of every account over the visible tracks
 */
const accountStatusesSelector = createSelector(visibleResultsSelector, whitelistSelector, getAccountStatuses);
/**
 * After filtering results by market configurator, filter by error filters and track filters
 */
const filteredResultsSelector = createSelector(marketConfiguratorResultsSelector, (s) => ({
    errorFilters: s.errorFilters,
    errorsOnly: s.errorsOnly,
    trackFilters: s.trackFilters,
}), (marketConfiguratorResults, filters) => {
    const { errorFilters, errorsOnly, trackFilters } = filters;
    return marketConfiguratorResults.filter(i => {
        if (errorsOnly && !i.isError) {
            return false;
        }
        return (!!trackFilters[i.trackId] &&
            (!i.error ||
                !errorFilters?.length ||
                !errorFilters.some(e => i.error?.toLowerCase().includes(e.toLowerCase()))));
    });
});
/**
 * React's dev-mode performance/devtools instrumentation serializes component
 * props via `JSON.stringify`, which throws on `bigint` values nested inside an
 * array. `ltChanges` (`Record<Address, [ltOld, ltNew]>`) is the only
 * array-of-bigints in the report, so stringify its entries here to keep the
 * denormalized result JSON-serializable for that tooling. Scalar bigints
 * elsewhere are serialized via `String(value)` and are unaffected.
 */
function sanitizeResult(r) {
    if ((r.strategy === "partial" || r.strategy === "deleverage") &&
        r.setup?.ltChanges) {
        const ltChanges = Object.fromEntries(Object.entries(r.setup.ltChanges).map(([token, [ltOld, ltNew]]) => [
            token,
            [ltOld.toString(), ltNew.toString()],
        ]));
        // Cast required because spreading a discriminated union member loses the
        // correlation between `strategy` and `setup`.
        return { ...r, setup: { ...r.setup, ltChanges } };
    }
    return r;
}
/**
 * Build tree of all credit managers, credit accounts and tracks
 * Credit managers are included even if they have no results
 * Respects market configurator in state
 */
export const treeSelector = createSelector(marketConfiguratorSelector, filteredResultsSelector, tracksSelector, tracksFiltersSelector, sdkStateSelector, (marketConfigurator, results, tracks, trackFilters, sdkState) => {
    const allCreditManagers = sdkState.markets
        .filter(m => !marketConfigurator ||
        hexEq(m.configurator, marketConfigurator))
        .flatMap(m => m.creditManagers)
        .flatMap(suite => suite.creditManager);
    const tracksById = Object.fromEntries(tracks.map(t => [t.id, t]));
    const tree = Object.fromEntries(allCreditManagers.map(cm => [cm.baseParams.addr.toLowerCase(), {}]));
    for (const r of results) {
        const { trackId, creditManager, account } = r;
        if (!trackFilters[trackId]) {
            continue;
        }
        const byCm = tree[creditManager.toLowerCase()] ?? {};
        const byCa = byCm[account.toLowerCase()] ?? {};
        byCa[trackId] = {
            ...sanitizeResult(r),
            track: tracksById[trackId],
        };
        byCm[account.toLowerCase()] = byCa;
        tree[creditManager.toLowerCase()] = byCm;
    }
    return tree;
});
/**
 * Build tracks stats - for all credit managers (of specific market configurator, if specified in state)
 * Or for specific credit manager
 */
export const statsSelector = createSelector(marketConfiguratorResultsSelector, tracksSelector, (_, creditManager) => creditManager, (results_, tracks, creditManager) => {
    const results = creditManager
        ? results_.filter(r => hexEq(r.creditManager, creditManager))
        : results_;
    const result = Object.fromEntries(tracks.map(t => [t.id, { liquidated: 0, discovered: 0, warning: 0 }]));
    for (const r of results) {
        if (result[r.trackId]) {
            result[r.trackId].discovered += 1;
            result[r.trackId].liquidated += r.isError ? 0 : 1;
            if (resultStatus(r) === "warning") {
                result[r.trackId].warning += 1;
            }
        }
    }
    return result;
});
/**
 * Per-role account stats over the full report, so every role tab has its own numbers whichever role is active.
 * Uses the same inputs as bad accounts: market configurator, track filters and service-only whitelist
 */
export const roleSummarySelector = createSelector(rawReportSelector, marketConfiguratorSelector, tracksFiltersSelector, (report, marketConfigurator, trackFilters) => {
    const sdkState = report.sdkState;
    return reportRoles(report).map(role => {
        const results = filterResultsByMarketConfigurator(filterReportByRole(report, role).results, sdkState, marketConfigurator).filter(r => !!trackFilters[r.trackId]);
        const whitelist = role === "service" ? buildWhitelist(report) : new AddressMap();
        const statuses = getAccountStatuses(results, whitelist)
            .values()
            .map(s => s.status);
        let status = "success";
        if (statuses.includes("error")) {
            status = "error";
        }
        else if (statuses.includes("warning")) {
            status = "warning";
        }
        return {
            role,
            total: statuses.length,
            success: statuses.filter(s => s === "success").length,
            status,
        };
    });
});
/**
 * Returns accounts that cannot be liquidated by any of visible tracks
 * Considers selected market configurator in state
 */
export const badAccountsSelector = createSelector(accountStatusesSelector, (statuses) => statuses.values().filter(s => s.status === "error"));
/**
 * Constructs gas usage histogram for particular track
 * Considers selected market configurator in state
 */
export const gasHistogramSelector = createSelector(marketConfiguratorResultsSelector, (_, selectedTrackId) => selectedTrackId, (results, selectedTrackId) => {
    const input = [];
    for (const r of results) {
        if (r.trackId === selectedTrackId && r.gasUsed) {
            input.push(100_000 * Math.round(Number(r.gasUsed) / 100_000));
        }
    }
    const nBins = Math.floor(input.length / 10);
    if (nBins === 0) {
        return null;
    }
    const buckets = bin()(input);
    return buckets.map(b => ({
        x0: b.x0,
        count: b.length,
        label: histogramLabel(b.x0),
        tooltip: `${histogramLabel(b.x0)} - ${histogramLabel(b.x1)}`,
    }));
});
/**
 * Computes total gas cost to liquidate all accounts for a track
 * Sums gasUsed of successful liquidations and multiplies by report gas price
 * Considers selected market configurator in state
 */
export const gasCostSummarySelector = createSelector(marketConfiguratorResultsSelector, reportSelector, (_, selectedTrackId) => selectedTrackId, (results, report, selectedTrackId) => {
    let count = 0;
    let totalGas = 0n;
    for (const r of results) {
        if (r.trackId === selectedTrackId && r.gasUsed) {
            count += 1;
            totalGas += BigInt(r.gasUsed);
        }
    }
    const gasPrice = BigInt(report.gasPrice ?? 0);
    return {
        count,
        totalGas,
        gasPrice,
        totalCostWei: totalGas * gasPrice,
    };
});
/**
 * Build denormalized results tree - what's displayed in report body
 */
export function selectDenormalizedResults(tree, sdkState, whitelist, accountLabels, statuses) {
    const result = [];
    for (const [cm, cmResults] of TypedObjectUtils.entries(tree)) {
        const details = describeCreditManager(sdkState, cm);
        if (!details) {
            console.warn(`credit manager ${cm} not found in SDK`);
            continue;
        }
        const accounts = [];
        let hasFailedAccounts = false;
        for (const [account, accTracks] of TypedObjectUtils.entries(cmResults)) {
            const whitelisted = isAccountWhitelisted(whitelist, cm, account, Object.values(accTracks));
            const status = statuses.get(account)?.status ?? "warning";
            accounts.push({
                account,
                whitelisted,
                status,
                label: accountLabels.get(account),
                tracks: accTracks,
            });
            hasFailedAccounts = hasFailedAccounts || status === "error";
        }
        result.push({
            name: details.name,
            underlying: details.underlying,
            address: cm,
            hasFailedAccounts,
            accounts,
        });
    }
    return result.sort((a, b) => b.accounts.length - a.accounts.length);
}
export const filteredDenormalizedResultsSelector = createSelector(treeSelector, sdkStateSelector, whitelistSelector, accountLabelsSelector, accountStatusesSelector, selectDenormalizedResults);
